import { Component, ElementRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AfterViewInit, OnDestroy } from '@angular/core';
import { Surface, Path, geometry, drawDOM, exportImage } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';

const { Point } = geometry;

@Component({
    selector: 'asap-signature',
    templateUrl: './signature.html',
    styleUrls: ['./signature.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SignatureComponent implements AfterViewInit, OnDestroy {
  @ViewChild('surface', { static: false })
  private surfaceElement: ElementRef;
  private surface: Surface;
  private path: Path;
  private isDrawing: boolean = false;
  _signatureValue: string = null;

  public ngAfterViewInit(): void {
    this.surface = Surface.create(this.surfaceElement.nativeElement);

    const el = this.surfaceElement.nativeElement;
    el.addEventListener('mousemove', this.onMouseMove);
    el.addEventListener('mousedown', this.onMouseDown);
    el.addEventListener('mouseup', this.onMouseUp);
    el.addEventListener('mouseleave', this.onMouseUp);

    el.addEventListener('touchmove', this.onTouchMove, { passive: false });
    el.addEventListener('touchstart', this.onTouchStart, { passive: false });
    el.addEventListener('touchend', this.onTouchEnd);
  }

  public ngOnDestroy() {
    const el = this.surfaceElement.nativeElement;
    if (el) {
      el.removeEventListener('mousemove', this.onMouseMove);
      el.removeEventListener('mousedown', this.onMouseDown);
      el.removeEventListener('mouseup', this.onMouseUp);
      el.removeEventListener('mouseleave', this.onMouseUp);

      el.removeEventListener('touchmove', this.onTouchMove);
      el.removeEventListener('touchstart', this.onTouchStart);
      el.removeEventListener('touchend', this.onTouchEnd);
    }
    if (this.surface) {
      this.surface.destroy();
    }
  }

  public onClear() {
    this.isDrawing = false;
    this.path = null;
    if (this.surface) {
      this.surface.clear();
    }
    this._signatureValue = null;
  }

  public export() {
    drawDOM(this.surfaceElement.nativeElement)
      .then(function (group) {
        return exportImage(group);
      }).then(function (data) {
        saveAs(data, 'signature.png');
      });
  }

  public renderAsPNG(): Promise<string> {
    return drawDOM(this.surfaceElement.nativeElement)
      .then((group) => exportImage(group))
      .then((data) => {
        this._signatureValue = data;
        return data;
      });
  }

  private getPoint(e: any): geometry.Point {
    const rect = this.surfaceElement.nativeElement.getBoundingClientRect();
    let clientX: number;
    let clientY: number;

    if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return new Point(clientX - rect.left, clientY - rect.top);
  }

  private startDrawing(e: any) {
    this.isDrawing = true;
    this.path = new Path({
      stroke: {
        color: '#E4141B',
        width: 2,
        lineCap: 'round',
        lineJoin: 'round'
      }
    });

    const newPoint = this.getPoint(e);
    this.path.moveTo(newPoint);
    this.path.lineTo(newPoint);
    this.surface.draw(this.path);
  }

  private draw(e: any) {
    if (!this.isDrawing || !this.path) {
      return;
    }
    const newPoint = this.getPoint(e);
    this.path.lineTo(newPoint);
  }

  private stopDrawing() {
    if (this.isDrawing) {
      this.isDrawing = false;
      this.renderAsPNG();
    }
  }

  private onMouseDown = (e: MouseEvent) => {
    this.startDrawing(e);
  };

  private onMouseMove = (e: MouseEvent) => {
    this.draw(e);
  };

  private onMouseUp = (e: MouseEvent) => {
    this.stopDrawing();
  };

  private onTouchStart = (e: TouchEvent) => {
    e.preventDefault();
    this.startDrawing(e);
  };

  private onTouchMove = (e: TouchEvent) => {
    e.preventDefault();
    this.draw(e);
  };

  private onTouchEnd = (e: TouchEvent) => {
    this.stopDrawing();
  };
}